import { useEffect } from 'react';
import styled from 'styled-components';

import Box from './Box';
import { Heading } from './Heading';

import CustomIcon from '../Icons/CustomIcon';

import { useResponsiveContext } from '../../../contexts/ResponsiveContext';
import lockScrollbar from '../../../utils/helpers/lockScrollbar';

const Backdrop = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    z-index: 100;
    left: 0;
    top: 0;
    background-color: #212121b2;
    transition: all 0.5s ease-in-out;
`;

type TScModalDesktop = {
    fullScreen?: boolean;
}
const ScModalDesktop = styled.div<TScModalDesktop>`
    position: fixed;
    z-index: 500;
    min-height: ${({ fullScreen }) => (fullScreen ? '100vh' : '20vh')};
    min-width: ${({ fullScreen }) => (fullScreen ? '100vw' : '40vw')};
    background-color: white;
    border-radius: ${({ theme, fullScreen }) => fullScreen ? 'none' : theme.radius.md};
    box-shadow: ${(props) => props.theme.shadow.level2};
    transform: translate(-50%, -50%);
    transform-origin: left;
    top: calc(50% - 1px);
    left: 50%;
    box-sizing: border-box;
    transition: all 0.3s ease-out;
`;

type TScModalMobile = {
    dynamic?: boolean;
}

const ScModalMobile = styled.div<TScModalMobile>`
    position: fixed;
    width: 100%;
    height: ${({ dynamic }) => dynamic ? 'auto' : '100%'};
    left: 0;
    bottom: 0;
    z-index: 500;
    background-color: white;
    border-radius: ${({ dynamic }) => dynamic ? '12px 12px 0px 0px' : 'none'};
    box-shadow: ${({ dynamic }) => dynamic ? '0px 4px 16px rgba(0, 0, 0, 0.15);' : 'none'};
    transition: transform 0.5s ease-in-out, z-index 0.5s ease-in-out;
`;

const CloseButton = styled.button`
    border: none;
    background-color: transparent;
    cursor: pointer;
    z-index: 1001;
`

type ModalProps = {
    show: boolean;
    fullScreen?: boolean;
    dynamic?: boolean;
    onClose?: () => void;
    children: React.ReactNode;
};

export const Modal = (props: ModalProps) => {
    const [viewMode] = useResponsiveContext();
    const isMobile = viewMode === 'mobile';

    useEffect(() => {
        if (props.show) {
            lockScrollbar(true);
        } else {
            lockScrollbar(false);
        }
    }, [props.show]);

    let backdrop = (
        <Backdrop
            style={{
                opacity: props.show ? '1' : '0',
                zIndex: props.show ? '500' : '-1',
            }}
            onClick={props.onClose}
        />
    );

    let modal = (
        <ScModalDesktop
            fullScreen={props.fullScreen}
            style={{
                transform: props.show ? 'scale(1) translate(-50%, -50%)' : 'scale(0.65) translate(-50%, -85%)',
                opacity: props.show ? '1' : '0',
                zIndex: props.show ? '501' : '-1',
            }}>
            {props.children}
        </ScModalDesktop>
    );

    let mobileModal = (
        <ScModalMobile
            dynamic={props.dynamic}
            style={{
                transform: props.show ? 'translateY(0)' : 'translateY(100vh)',
                zIndex: props.show ? '501' : '0',
            }}
        >
            {props.children}
        </ScModalMobile>
    );

    return (
        <>
            {isMobile ? props.dynamic ? backdrop : null : backdrop}
            {isMobile ? mobileModal : modal}
        </>
    );
};


type TModalHeaderProps = {
    title?: string,
    onClose: () => void;
}

export const ModalHeader = ({ title, onClose }: TModalHeaderProps) => {
    return (
        <Box display="flex" p="lg" justifyContent={title ? "space-between" : 'end'} alignItems="center">
            {title &&
                <Box>
                    <Heading level={1} typography="text-xl-semibold">
                        {title}
                    </Heading>
                </Box>
            }
            <CloseButton onClick={onClose}>
                <CustomIcon icon="ActionClose_sm" customColor='black-1' />
            </CloseButton>
        </Box>
    )
}
